// **React Imports
import { memo, useCallback, useMemo } from 'react'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import { setCandidateAppMode } from '../../../store/slice/candidate'

// **MUI Imports
import { ToggleButton, ToggleButtonGroup } from '@mui/material'

// **Utils Imports
import { CANDIDATE_APP_MODE } from '../../../utils/constant'
import {
  getJWTToken,
  getRememberMeFlag,
  setCandidateAppModeInStorage,
  setCookie
} from '../../../utils/utils'

// **Components Imports
import Icon from '../../icon'

// **SCSS Imports
import { ENVIRONMENT } from '../../../env/env'
import './index.scss'
import { useNavigate } from 'react-router-dom'

const CandidateAppModeSwitch = ({ addBackgroundColor }) => {
  // hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // states
  const appMode = useSelector((state) => state.candidate.candidateAppMode)

  // variables
  const currentSubdomainLabel = useMemo(() => {
    if (window.location.hostname === 'localhost') {
      return 'localhost'
    }

    const hostNameParts = window.location.hostname?.split('.')

    if (hostNameParts?.length <= 2) {
      return null
    }

    return hostNameParts[0].toLowerCase()
  }, [])

  // handlers
  const handleCookies = useCallback((mode) => {
    const currentToken = getJWTToken()
    const rememberMeFlag = getRememberMeFlag() || false

    if (currentToken) {
      setCookie('user-token', currentToken, {
        maxAge: 1 * 24 * 60
      })
      setCookie('remember-me-flag', rememberMeFlag, {
        maxAge: 1 * 24 * 60
      })

      setCandidateAppModeInStorage(mode)
    }
  }, [])

  const navigateToAppSubdomain = useCallback(() => {
    const protocol = window.location.protocol
    const port = window.location.port ? `:${window.location.port}` : ''
    const linkToAppend = '/c'

    const redirectionLink = `${protocol}//app.coderscout.io${port}${linkToAppend}`

    window.location.href = redirectionLink
  }, [])

  const handleModeChange = useCallback(
    (_, newMode) => {
      if (!newMode || newMode === appMode) {
        return
      }

      if (newMode === CANDIDATE_APP_MODE.INTERVIEW) {
        dispatch(setCandidateAppMode(newMode))
        if (window.location.pathname !== '/c') {
          navigate('/c', { replace: true })
        }
        return
      }

      const isNonAppSubdomain =
        currentSubdomainLabel && currentSubdomainLabel !== 'app'

      if (isNonAppSubdomain && ENVIRONMENT === 'PRODUCTION') {
        handleCookies(newMode)
        navigateToAppSubdomain()
      } else {
        dispatch(setCandidateAppMode(newMode))
        if (window.location.pathname !== '/c') {
          navigate('/c', { replace: true })
        }
      }
    },
    [
      appMode,
      currentSubdomainLabel,
      dispatch,
      navigate,
      handleCookies,
      navigateToAppSubdomain
    ]
  )

  return (
    <ToggleButtonGroup
      className={`candidate-app-mode-switch ${addBackgroundColor ? 'has-bg' : ''}`}
      value={appMode}
      exclusive
      onChange={handleModeChange}
      aria-label="candidate application mode"
    >
      <ToggleButton
        value={CANDIDATE_APP_MODE.INTERVIEW}
        aria-label="interview mode"
      >
        <Icon icon="streamline-plump:office-worker-solid" fontSize="1.125rem" />
        Interview
      </ToggleButton>
      <ToggleButton
        value={CANDIDATE_APP_MODE.PRACTICE}
        aria-label="practice mode"
      >
        <Icon icon="fluent:brain-sparkle-32-regular" fontSize="1.125rem" />
        Prep
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

export default memo(CandidateAppModeSwitch)
